// src/plugins/attachment-button.plugin.js
import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import ButtonView from '@ckeditor/ckeditor5-ui/src/button/buttonview';

// Default SVG fallback (only used if no PNG icon is provided)
const DEFAULT_PAPERCLIP_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 485 485">
<g>
 <path d="M242.5,485C172.196,485,115,427.804,115,357.5v-265h30v265c0,53.762,43.738,97.5,97.5,97.5s97.5-43.738,97.5-97.5v-265
  c0-34.463-28.038-62.5-62.5-62.5S215,58.037,215,92.5v265c0,15.164,12.336,27.5,27.5,27.5s27.5-12.336,27.5-27.5v-265h30v265
  c0,31.706-25.794,57.5-57.5,57.5S185,389.206,185,357.5v-265C185,41.495,226.495,0,277.5,0S370,41.495,370,92.5v265
  C370,427.804,312.804,485,242.5,485z"/>
</g>
</svg>`;

export default class AttachmentButtonPlugin extends Plugin {
	static get pluginName() {
		return 'AttachmentButtonPlugin';
	}

	init() {
		const editor = this.editor;
		const t = editor.t;

		const cfg = editor.config.get( 'attachmentButton' ) || {};

		/**
		 * mode:
		 *  - 'file'     → OLD behavior (default)
		 *  - 'callback' → just call onClick(editor)
		 *  - 'event'    → fire editor event
		 */
		const mode = cfg.mode || 'file';

		const iconSrc = typeof cfg.iconSrc === 'string' ? cfg.iconSrc : null;
		const accept = typeof cfg.accept === 'string' ? cfg.accept : '';
		const multiple = cfg.multiple !== false;
		const validate = typeof cfg.validate === 'function' ? cfg.validate : null;
		const onSelect = typeof cfg.onSelect === 'function' ? cfg.onSelect : null;
		const onClick = typeof cfg.onClick === 'function' ? cfg.onClick : null;

		editor.ui.componentFactory.add( 'attachment', locale => {
			const view = new ButtonView( locale );

			view.set( {
				label: t( 'Attach' ),
				tooltip: true,
				withText: false
			} );

			// Add common class for BOTH svg & image ico.ns
			view.extendTemplate( {
				attributes: {
					class: [ 'ck-attachment-button--img' ]
				}
			} );

			// ---------------- ICON HANDLING ----------------
			// SVG → use CKEditor icon pipeline
			if ( iconSrc && iconSrc.trim().indexOf( '<svg' ) === 0 ) {
				view.icon = iconSrc;
			}

			// PNG / JPG → render via CSS background (OLD BEHAVIOR)
			if ( iconSrc && iconSrc.trim().indexOf( '<svg' ) !== 0 ) {
				view.on( 'render', () => {
					const el = view.element;
					if ( !el ) {
						return;
					}

					el.style.backgroundImage = 'url(' + iconSrc + ')';
					el.style.backgroundRepeat = 'no-repeat';
					el.style.backgroundPosition = 'center';
					el.style.backgroundSize = '18px 18px';
				} );
			}

			// Fallback SVG if no iconSrc at all
			if ( !iconSrc ) {
				view.icon = DEFAULT_PAPERCLIP_SVG;
			}

			// ---------------- FILE MODE (OLD DEFAULT) ----------------
			if ( mode === 'file' ) {
				// eslint-disable-next-line no-undef
				const fileInput = document.createElement( 'input' );
				fileInput.type = 'file';
				fileInput.hidden = true;
				fileInput.multiple = multiple;
				if ( accept ) {
					fileInput.accept = accept;
				}

				fileInput.addEventListener( 'change', () => {
					const files = Array.prototype.slice.call( fileInput.files || [] );
					fileInput.value = '';

					if ( !files.length ) {
						return;
					}

					if ( validate ) {
						try {
							if ( !validate( files ) ) {
								this._notify( editor, '❌ Invalid file type or size.' );
								return;
							}
						} catch ( e ) {
							this._notify( editor, '⚠️ File validation failed.' );
							return;
						}
					}

					if ( onSelect ) {
						try {
							onSelect( files, editor );
						} catch ( e ) {
							// this._notify( editor, '⚠️ File handler failed.' );
						}
					} else {
						editor.fire( 'attachment:selected', { files } );
					}
				} );

				view.on( 'execute', () => fileInput.click() );
				return view;
			}

			// ---------------- CALLBACK MODE ----------------
			if ( mode === 'callback' ) {
				view.on( 'execute', () => {
					try {
						if ( !onClick ) {
							throw new Error( 'attachmentButton.mode="callback" requires onClick' );
						}
						onClick( editor );
					} catch ( e ) {
						this._notify( editor, '⚠️ Attachment action failed.' );
					}
				} );
				return view;
			}

			// ---------------- EVENT MODE ----------------
			if ( mode === 'event' ) {
				view.on( 'execute', () => {
					editor.fire( 'attachment:click' );
				} );
				return view;
			}

			return view;
		} );
	}

	_notify( editor, message, type ) {
		try {
			let notification = null;

			try {
				if ( editor.plugins && editor.plugins.has( 'Notification' ) ) {
					notification = editor.plugins.get( 'Notification' );
				}
			} catch ( e ) {
				notification = null;
			}

			if ( notification && typeof notification.showWarning === 'function' ) {
				notification.showWarning( message, {
					title: 'Attachment',
					namespace: 'attachment',
					type: type || 'warning'
				} );
			} else if ( typeof console !== 'undefined' ) {
				// eslint-disable-next-line no-undef
				console.warn( '[AttachmentButton]', message );
			}
		} catch ( e ) {
			// no-op
		}
	}
}
